// src/pages/team/TeamBrandingPage.tsx

import { useEffect, useState, useCallback, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { observer } from '@react';
import { useTeam, useI18n } from '@react/hooks';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Alert,
  ErrorAlert,
} from '@react/components';
import { AvatarUploader } from '../../components/AvatarUploader';
import { validateAvatar, validateBanner } from '../../features/userprofile/logic';
import { formatDateTime } from '@/features/team/logic';
import { TeamSectionNav } from '@/features/team/TeamSectionNav';
import { uploadAttachment } from '@/features/chat/logic';
import { attachmentApi, teamApi } from '@core/api';

/**
 * The team service stores avatar/banner as plain URLs (CreateTeamAvatarRequest
 * carries TeamAvatar{url}, no file handling in the handler) — so upload the
 * file through the attachment service first and pass its download URL.
 */
async function fileToUrl(file: File): Promise<string> {
  const { entityId } = await uploadAttachment(file);
  const res = await attachmentApi.GetDownloadUrl(entityId);
  const url = res.download_url?.url;
  if (!url) throw new Error('No download URL for uploaded file');
  return url;
}

export const TeamBrandingPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const store = useTeam();
  const { t } = useI18n();

  const [avatars, setAvatars] = useState<any[]>([]);
  const [banner, setBanner] = useState<any>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [viewingAvatar, setViewingAvatar] = useState<any | null>(null);
  const [viewingAvatarLoading, setViewingAvatarLoading] = useState(false);
  const bannerInputRef = useRef<HTMLInputElement>(null);

  // teamApi directly: a 404 for "no banner uploaded yet" is a normal empty
  // state and must not land in store.error as a page-level error alert.
  const loadAvatars = useCallback(async () => {
    if (!id) return;
    try {
      const res = await teamApi.ListTeamAvatars(id);
      setAvatars(res.avatars?.avatars ?? []);
    } catch { /* empty gallery */ }
  }, [id]);

  const loadBanner = useCallback(async () => {
    if (!id) return;
    try {
      const res = await teamApi.GetTeamBanner(id);
      setBanner(res.banner ?? null);
    } catch {
      setBanner(null);
    }
  }, [id]);

  useEffect(() => {
    if (id) store.GetTeam(id);
    loadAvatars();
    loadBanner();
  }, [id, loadAvatars, loadBanner]);

  const showSuccess = (msg: string) => {
    setSuccessMessage(msg);
    setError(null);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const showError = (err: unknown) => {
    setError(err instanceof Error ? err.message : String(err));
    setSuccessMessage(null);
  };

  const handleAvatarUpload = async (file: File) => {
    if (!id) return;
    const validation = validateAvatar(file);
    if (Object.keys(validation).length > 0) {
      setError(t(Object.values(validation)[0]));
      return;
    }
    try {
      const url = await fileToUrl(file);
      await store.CreateTeamAvatar(id, { avatar: { url, thumbnail_url: url, is_active: false } });
      showSuccess(t('team.avatarUploaded'));
      loadAvatars();
    } catch (err) { showError(err); }
  };

  const handleActivateAvatar = async (avatarId: string) => {
    if (!id) return;
    try {
      await store.ActivateTeamAvatar(id, avatarId, {});
      showSuccess(t('team.avatarActivated'));
      loadAvatars();
    } catch { /* store handles */ }
  };

  const handleViewAvatar = async (avatarId: string) => {
    if (!id) return;
    setViewingAvatarLoading(true);
    try {
      const res = await store.GetTeamAvatar(id, avatarId);
      setViewingAvatar(res?.avatar ?? null);
    } catch { /* store handles */ } finally { setViewingAvatarLoading(false); }
  };

  const handleDeleteAvatar = async (avatarId: string) => {
    if (!id || !confirm(t('team.deleteAvatarConfirm'))) return;
    try {
      await store.DeleteTeamAvatar(id, avatarId);
      showSuccess(t('team.avatarDeleted'));
      loadAvatars();
    } catch { /* store handles */ }
  };

  const handleBannerUpload = async (file: File) => {
    if (!id) return;
    const validation = validateBanner(file);
    if (Object.keys(validation).length > 0) {
      setError(t(Object.values(validation)[0]));
      return;
    }
    try {
      const url = await fileToUrl(file);
      await store.CreateTeamBanner(id, { banner: { url, is_active: true } });
      showSuccess(t('team.bannerUpdated'));
      loadBanner();
    } catch (err) { showError(err); }
  };

  const handleDeleteBanner = async () => {
    if (!id || !confirm(t('team.deleteBannerConfirm'))) return;
    try {
      await store.DeleteTeamBanner(id);
      showSuccess(t('team.bannerDeleted'));
      setBanner(null);
    } catch { /* store handles */ }
  };

  const teamName = (store.currentItem as any)?.name || t('team.title');

  return (
    <div className="space-y-6">
      <nav className="text-sm">
        <Link to="/teams" className="text-muted-foreground hover:text-foreground">{t('team.list')}</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to={`/teams/${id}`} className="text-muted-foreground hover:text-foreground">{teamName}</Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <span className="text-foreground">{t('team.nav.branding')}</span>
      </nav>
      <TeamSectionNav teamId={id ?? ''} group="settings" active="branding" />


      <h1 className="text-2xl font-bold text-foreground">{t('team.brandingTitle')}</h1>

      {store.error && <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />}
      {error && <ErrorAlert error={error} dismissible onDismiss={() => setError(null)} />}
      {successMessage && <Alert variant="success" dismissible onDismiss={() => setSuccessMessage(null)}>{successMessage}</Alert>}

      {/* Avatars */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>{t('team.avatarTitle')}</CardTitle>
            <CardDescription>{t('team.avatarDescription')}</CardDescription>
          </div>
          <AvatarUploader
            onFileSelect={handleAvatarUpload}
            className="!gap-0 !flex-row"
          />
        </CardHeader>
        <CardContent>
          {avatars.length === 0 ? (
            <div className="text-center py-8">
              <div className="mx-auto w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-3">
                <svg className="w-6 h-6 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <p className="text-sm text-muted-foreground">{t('team.noAvatars')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
              {avatars.map((av: any) => {
                const avId = av.entity?.id || av.id || '';
                const imgUrl = av.thumbnail_url || av.url;
                return (
                  <div key={avId} className="relative group">
                    <div
                      className={`w-full aspect-square rounded-xl overflow-hidden border-2 transition-all ${
                        av.is_active
                          ? 'border-primary ring-2 ring-primary/20'
                          : 'border-transparent hover:border-primary/50'
                      }`}
                    >
                      <img src={imgUrl} alt="Avatar" className="w-full h-full object-cover" />
                    </div>
                    <div className="absolute inset-0 rounded-xl bg-foreground/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1.5 p-2">
                      <div className="flex gap-1.5">
                        <Button size="sm" variant="secondary" onClick={() => handleViewAvatar(avId)} className="px-2 py-1 h-auto" title={t('team.avatarInfo')}>
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </Button>
                        {!av.is_active && (
                          <Button size="sm" variant="secondary" onClick={() => handleActivateAvatar(avId)} className="text-xs px-2 py-1 h-auto">
                            {t('common.use')}
                          </Button>
                        )}
                        <Button size="sm" variant="danger" onClick={() => handleDeleteAvatar(avId)} className="px-2 py-1 h-auto">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </Button>
                      </div>
                    </div>
                    {av.is_active && (
                      <div className="absolute -top-1.5 -right-1.5 flex items-center justify-center w-5 h-5 bg-primary rounded-full border-2 border-card">
                        <svg className="w-3 h-3 text-primary-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Banner */}
      <Card>
        <CardHeader>
          <CardTitle>{t('team.bannerTitle')}</CardTitle>
          <CardDescription>{t('team.bannerDescription')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {banner?.url ? (
            <div className="relative group">
              <img
                src={banner.url}
                alt="Banner"
                className="w-full h-40 object-cover rounded-lg border border-border"
              />
              <div className="absolute inset-0 rounded-lg bg-foreground/0 group-hover:bg-foreground/30 transition-colors flex items-center justify-center gap-2">
                <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button size="sm" variant="secondary" onClick={() => bannerInputRef.current?.click()} className="shadow-md">
                    <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    {t('team.changeBanner')}
                  </Button>
                  <Button size="sm" variant="danger" onClick={handleDeleteBanner} className="shadow-md">
                    {t('common.remove')}
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <button
              type="button"
              className="w-full h-40 rounded-lg border-2 border-dashed border-border flex items-center justify-center cursor-pointer hover:border-primary/50 transition-colors"
              onClick={() => bannerInputRef.current?.click()}
            >
              <span className="text-center">
                <svg className="w-8 h-8 mx-auto text-muted-foreground mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                <p className="text-sm text-muted-foreground">{t('team.uploadBanner')}</p>
                <p className="text-xs text-muted-foreground mt-1">JPG, PNG or WebP. Max 10MB.</p>
              </span>
            </button>
          )}
          <input
            ref={bannerInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleBannerUpload(f);
              e.target.value = '';
            }}
          />
        </CardContent>
      </Card>

      {(viewingAvatar || viewingAvatarLoading) && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          role="button"
          tabIndex={0}
          onClick={() => setViewingAvatar(null)}
          onKeyDown={(e) => { if (e.key === 'Escape' || e.key === 'Enter') setViewingAvatar(null); }}
        >
          <div className="absolute inset-0 bg-foreground/50 backdrop-blur-sm" />
          <Card className="relative w-full max-w-sm" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
            <CardHeader>
              <CardTitle>{t('team.avatarInfo')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {viewingAvatarLoading || !viewingAvatar ? (
                <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
              ) : (
                <>
                  <img
                    src={(viewingAvatar as any).url || (viewingAvatar as any).thumbnail_url}
                    alt="Avatar"
                    className="w-24 h-24 rounded-xl object-cover mx-auto"
                  />
                  <dl className="divide-y divide-border">
                    <div className="py-2 grid grid-cols-3 gap-4">
                      <dt className="text-sm font-medium text-muted-foreground">{t('team.avatarInfoId')}</dt>
                      <dd className="text-sm text-foreground col-span-2 font-mono truncate">{(viewingAvatar as any).entity?.id || (viewingAvatar as any).id || '--'}</dd>
                    </div>
                    <div className="py-2 grid grid-cols-3 gap-4">
                      <dt className="text-sm font-medium text-muted-foreground">{t('team.avatarInfoCreated')}</dt>
                      <dd className="text-sm text-foreground col-span-2">{formatDateTime((viewingAvatar as any).created_at)}</dd>
                    </div>
                    <div className="py-2 grid grid-cols-3 gap-4">
                      <dt className="text-sm font-medium text-muted-foreground">{t('team.avatarInfoActive')}</dt>
                      <dd className="text-sm text-foreground col-span-2">{(viewingAvatar as any).is_active ? t('common.yes') : t('common.no')}</dd>
                    </div>
                  </dl>
                </>
              )}
              <div className="flex justify-end pt-2">
                <Button variant="secondary" onClick={() => setViewingAvatar(null)}>{t('common.close')}</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
});

export default TeamBrandingPage;
